import get from "lodash/get";
import React from "react";
import { Button, Pagination } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";
import { useHistory, useRouteMatch } from "react-router-dom";

import Wrapper from "~/shared/ui/Wrapper";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import { getFormatDate } from "~/libs/getFormatDate";
import {
  DealerReportStatus,
  GetDealerReportsSort,
  SortType,
  useGetAccountingsCheckingGettersQuery,
  useGetAccountingsDraftGettersQuery,
  useGetAccountingsFixingGettersQuery,
  useGetAccountingsGettersQuery,
  useGetAccountingsVerifiedGettersQuery,
} from "~/schemas/generated/graphql";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2, useTableSort } from "~/shared/ui/TableV2";

import AccountingDealerFilter from "./Filter";
import AccountingReportModal from "./modals/AccountingReportModal";
import {
  ACCOUNTING_DEALER_SORTABLE_COLUMNS,
  getAccountingDealerColumns,
} from "./model/tableAdapter";

import s from "./AccountingDealer.less";

/** Маппинг ключей колонок таблицы в enum сортировки backend'а */
const SORT_BY_MAP: Record<string, GetDealerReportsSort> = {
  status: GetDealerReportsSort.Status,
  updatedAt: GetDealerReportsSort.UpdatedAt,
  DealerName: GetDealerReportsSort.DealerName,
  lastDate: GetDealerReportsSort.PeriodLastDate,
  reportDate: GetDealerReportsSort.PeriodReportDate,
};

const AccountingDealer = () => {
  const { t } = useTranslation();
  const language = localStorage.getItem("language");
  const isEn = language === "en";

  const history = useHistory();
  const { params } = useRouteMatch();
  const { hasPermission } = usePermissions();

  const setData = useSetLocationData();
  // Открытие/закрытие модалок — через replace, чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);

  const {
    count,
    page,
    searchText,
    searchTextForSelect,
    sortField,
    sortType: sortTypeRaw,
  } = useGetLocationData();

  const { sortType, onSortChange } = useTableSort({
    sortField,
    sortType: sortTypeRaw,
    sortableColumns: ACCOUNTING_DEALER_SORTABLE_COLUMNS,
    setData,
  });

  const sortEnumValue = sortField ? SORT_BY_MAP[sortField] : undefined;

  const goBack = () => {
    history.replace("/accounting/");
  };

  const dealerNameFilter = searchText ? String(searchText) : undefined;
  const statusFilter =
    searchTextForSelect &&
    DealerReportStatus[searchTextForSelect as keyof typeof DealerReportStatus]
      ? DealerReportStatus[
          searchTextForSelect as keyof typeof DealerReportStatus
        ]
      : undefined;

  const { data, loading } = useGetAccountingsGettersQuery({
    variables: {
      ReportPeriodId: params["period"],
      dealerName: dealerNameFilter,
      status: statusFilter,
      sortBy: sortEnumValue,
      sortType: sortType
        ? SortType[sortType === "ASC" ? "Asc" : "Desc"]
        : undefined,
      page,
      count,
    },
    fetchPolicy: "no-cache",
  });

  const getDraft = useGetAccountingsDraftGettersQuery({
    variables: { ReportPeriodId: params["period"] },
    fetchPolicy: "no-cache",
  });
  const draftTotal = get(
    getDraft,
    "data.AccountingGetter.getDealerReports.total",
    0,
  );

  const getChecking = useGetAccountingsCheckingGettersQuery({
    variables: { ReportPeriodId: params["period"] },
    fetchPolicy: "no-cache",
  });
  const checkingTotal = get(
    getChecking,
    "data.AccountingGetter.getDealerReports.total",
    0,
  );

  const getVerified = useGetAccountingsVerifiedGettersQuery({
    variables: { ReportPeriodId: params["period"] },
    fetchPolicy: "no-cache",
  });
  const verifiedTotal = get(
    getVerified,
    "data.AccountingGetter.getDealerReports.total",
    0,
  );

  const getFixing = useGetAccountingsFixingGettersQuery({
    variables: { ReportPeriodId: params["period"] },
    fetchPolicy: "no-cache",
  });
  const fixingTotal = get(
    getFixing,
    "data.AccountingGetter.getDealerReports.total",
    0,
  );

  const totalsLoading =
    getDraft.loading &&
    getChecking.loading &&
    getVerified.loading &&
    getFixing.loading;

  const accountingsData = (
    get(data, "AccountingGetter.getDealerReports.items") ?? []
  ).map((el) => ({
    idDealer: el.DealerId,
    period: el.ReportPeriod.id,
    DealerName: isEn ? el.Dealer.DealerNameEn : el.Dealer.DealerName,
    lastDate: getFormatDate(el.ReportPeriod.lastDate, true),
    reportDate: getFormatDate(el.ReportPeriod.reportDate, true),
    status: t(`accounting.${el.status}`),
    updatedAt: getFormatDate(el.updatedAt, true),
    Files: el.Files,
  }));

  const accountingsTotal = get(
    data,
    "AccountingGetter.getDealerReports.total",
    0,
  );

  const openReportMonitoring = () =>
    setDataReplace("openModal", "ReportMonitoring");
  const openReportConditions = () =>
    setDataReplace("openModal", "ReportConditions");
  const openReportColumns = () => setDataReplace("openModal", "ReportColumns");

  const { handleCount, handlePage } = usePaginationParams();

  const onDealerNameClick = (record: { period: string; idDealer: string }) =>
    history.push(
      `/accounting/${record.period}/${record.idDealer}?searchTextForSelect=${
        searchTextForSelect || ""
      }`,
    );

  return (
    <Wrapper.Content
      title={t("accounting.dealer_register")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      filterComponent={<AccountingDealerFilter />}
      controlComponent={
        <div className={s.controlsBlock}>
          {hasPermission("accounting.getFileDealerMonitoring") && (
            <Button
              size={"middle" as any}
              color="success"
              onClick={openReportMonitoring}
            >
              {t("accounting.ReportMonitoring")}
            </Button>
          )}
          {hasPermission("accounting.getFileDealerConditions") && (
            <Button
              size={"middle" as any}
              color="success"
              onClick={openReportConditions}
            >
              {t("accounting.ReportConditions")}
            </Button>
          )}
          {hasPermission("accounting.getFileReportFileColumns") && (
            <Button
              size={"middle" as any}
              color="success"
              onClick={openReportColumns}
            >
              {t("accounting.ReportColumns")}
            </Button>
          )}
        </div>
      }
    >
      {!totalsLoading && hasPermission("accounting.getReportsStatistics") && (
        <div className={s.wrapperInfo}>
          <ul>
            <li>
              {t("accounting.Draft")} : {draftTotal}
            </li>
            <li>
              {t("accounting.Checking")} : {checkingTotal}
            </li>
            <li>
              {t("accounting.Verified")} : {verifiedTotal}
            </li>
            <li>
              {t("accounting.Fixing")} : {fixingTotal}
            </li>
          </ul>
        </div>
      )}

      <div className={s.paginationWrap}>
        <TableV2
          wrapperClassName={s.tableWrapper}
          columns={getAccountingDealerColumns(t, onDealerNameClick)}
          dataSource={accountingsData}
          loading={loading}
          rowKey="idDealer"
          scroll={{ x: "max-content" }}
          sortableColumns={ACCOUNTING_DEALER_SORTABLE_COLUMNS}
          sortField={sortField}
          sortType={sortType}
          onSortChange={onSortChange}
        />
        <Pagination
          count={count}
          page={page + 1}
          totalCount={accountingsTotal}
          onChangeCount={handleCount}
          onChangePage={handlePage}
        />
      </div>

      <AccountingReportModal mode="ReportMonitoring" />
      <AccountingReportModal mode="ReportConditions" />
      <AccountingReportModal mode="ReportColumns" />
    </Wrapper.Content>
  );
};

export default AccountingDealer;
